// Shared helpers for the legacy-parity report pages (ReportFormComponent / *ReportComponent).

import { hasTranslation, t } from '../../i18n';

export function num(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function money(value: unknown): string {
  if (value == null || value === '') return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function qty(value: unknown): string {
  if (value == null || value === '') return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function relName(value: unknown, fallback = '-'): string {
  if (!value || typeof value !== 'object') return fallback;
  const record = value as Record<string, unknown>;
  return String(record.name ?? record.label ?? record.title ?? fallback);
}

export function relNames(value: unknown): string {
  if (!Array.isArray(value)) return '-';
  const names = value.map((entry) => relName(entry, '')).filter(Boolean);
  return names.length ? names.join(', ') : '-';
}

export function isTruthyFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

/** Sum of `field` across a list of line records (sale_items / purchase_items). */
export function sumLines(lines: unknown, field: string): number {
  if (!Array.isArray(lines)) return 0;
  return lines.reduce((acc, line) => acc + num((line as Record<string, unknown>)?.[field]), 0);
}

/** Render extra_attributes (custom fields) as "key: value" pairs. */
export function extrasSummary(value: unknown): string {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '-';
  const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v != null && v !== '');
  return entries.length ? entries.map(([k, v]) => `${k}: ${String(v)}`).join(', ') : '-';
}

// ---------------------------------------------------------------------------
// Date range helpers (legacy DatePicker type="datetimerange" + shortcuts)
// ---------------------------------------------------------------------------

const pad = (n: number) => String(n).padStart(2, '0');

/** Date → value accepted by <input type="datetime-local"> (local time). */
export function toInputValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** datetime-local value → backend format "YYYY-MM-DD HH:mm:ss" (legacy datetimeFormatString). */
export function toBackendDateTime(value: string, end = false): string {
  if (!value) return '';
  const [date, time = end ? '23:59' : '00:00'] = value.split('T');
  return `${date} ${time.length === 5 ? `${time}:${end ? '59' : '00'}` : time}`;
}

function startOfDay(date: Date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date: Date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

export type DatePresetKey = 'today' | 'yesterday' | 'days7' | 'days30' | 'days60' | 'days90' | 'days180' | 'thisYear' | 'lastYear';

export const datePresets: DatePresetKey[] = ['today', 'yesterday', 'days7', 'days30', 'days60', 'days90', 'days180', 'thisYear', 'lastYear'];

/** Same ranges as the legacy ReportFormComponent shortcuts. */
export function presetRange(key: DatePresetKey, now = new Date()): [Date, Date] {
  const daysBack = (days: number): [Date, Date] => [startOfDay(new Date(now.getTime() - days * 24 * 3600 * 1000)), endOfDay(now)];
  switch (key) {
    case 'today':
      return [startOfDay(now), endOfDay(now)];
    case 'yesterday': {
      const y = new Date(now.getTime() - 24 * 3600 * 1000);
      return [startOfDay(y), endOfDay(y)];
    }
    case 'days7':
      return daysBack(7);
    case 'days30':
      return daysBack(30);
    case 'days60':
      return daysBack(60);
    case 'days90':
      return daysBack(90);
    case 'days180':
      return daysBack(180);
    case 'thisYear':
      // Legacy: Jan 1st → last day of the current month.
      return [startOfDay(new Date(now.getFullYear(), 0, 1)), endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0))];
    case 'lastYear':
      return [startOfDay(new Date(now.getFullYear() - 1, 0, 1)), endOfDay(new Date(now.getFullYear(), 0, 0))];
    default:
      return daysBack(90);
  }
}

/** Backend default when no range is sent: now()->subMonths(3)->startOfDay() → now()->endOfDay(). */
export function defaultRange(now = new Date()): [Date, Date] {
  const start = new Date(now);
  start.setMonth(start.getMonth() - 3);
  return [startOfDay(start), endOfDay(now)];
}

/** Translate an enum value (gateway, status, adjustment type…) via report.value.*, else humanize it. */
export function valueLabel(value: unknown): string {
  if (value == null || value === '') return '-';
  const raw = String(value);
  const key = `report.value.${raw}`;
  return hasTranslation(key) ? t(key) : raw.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}
